import { useEffect, useMemo } from 'react';
import '../../styles/connections.css';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAccessToken } from '../../hooks/useAccessToken';
import { SweetAlertIcon } from '../../utils/Constants';
import { useColorModeContext } from '../../context/ColorModeContext';
import { useUsersConnections } from '../../hooks/useUsersConnections';
import { classNames, generateIcon } from '../../utils/Utils';
import { COLUMNS_ACCOUNT_CONNECTIONS } from '../../utils/TableColumnsConstants';

import { Button, ButtonGroup, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import TableComponent from '../../components/TableComponent';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useUserContext } from '../../context/UserContext'
import { userConnectionService } from '../../services/UserConnectionService'

interface IAccount {
  id: string;
  name: string;
  email: string;
  profile_url: string;
}

export const AccountConnectionsPage = () => {
  const { t } = useTranslation();
  const token = useAccessToken();
  const { myUser } = useUserContext();
  const { mode } = useColorModeContext();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // When return from google auth add account to db
    const code = searchParams.get('code');
    if (code && token) {
      // clean params
      window.history.replaceState({}, document.title, window.location.pathname);

      const handleCode = async () => {

        const params = {
          code: searchParams.get('code'),
          redirectUri: window.location.href.split('?')[0]
        }

        try {
          const data = await userConnectionService.addAccountToDatabase({ accessToken: token, params });
          if(data) {
            Swal.fire({
              title: t('Yes...'),
              icon: SweetAlertIcon.SUCCESS,
              text: t('your_account_has_been_successfully_connected')
            });
            getAccounts();
          } else {
            Swal.fire({
              title: 'Oh...',
              icon: SweetAlertIcon.INFO,
              text: t('error_occurred_connect_account')
            });
          }
        } finally {
          searchParams.delete('code');
          setSearchParams(searchParams);
        }
      };

      handleCode();
    }
  }, []);

  const handleOnboardingAddAccountClick = () => {
    Swal.fire({
      title: t('connect_new_account'),
      text: t('you_are_going_to_be_redirected_to_google'),
      icon: SweetAlertIcon.INFO,
      showCloseButton: true,
      showCancelButton: true,
      cancelButtonText: t('cancel'),
      confirmButtonText: t('go_to_google')
    }).then(async (result) => {
      const redirectUri = window.location.href.split('?')[0]
      if (result.isConfirmed) {
        const state = myUser.sub;
        window.location.href = `https://accounts.google.com/o/oauth2/auth/oauthchooseaccount?response_type=code&client_id=575090919591-7snho08ckt9qbts5apffudk57t5bp895.apps.googleusercontent.com&redirect_uri=${redirectUri}&scope=${'https://www.googleapis.com/auth/webmasters.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'}&state=${state}&prompt=consent&access_type=offline`;
      }
    });
  }

  const {
    error,
    loading,
    connectedAccounts,
    getAccounts,
    deleteAccounts,
  } = useUsersConnections();

  const handleDeleteAccountSwal = (account: IAccount) => {
    Swal.fire({
      title: t('are_you_delete_account'),
      icon: SweetAlertIcon.WARNING,
      showCloseButton: true,
      showCancelButton: true,
      cancelButtonText: t('cancel'),
      confirmButtonText: t('delete'),
      confirmButtonColor: '#de3030'
    }).then(async (result) => {
      if (result.isConfirmed) await handleDeleteAccount(account.id);
    });
  };

  const handleDeleteAccount = async (id: string) => {
    try {
      const res = await deleteAccounts(id);
      if (res) {
        Swal.fire({
          title: t('Yes...'),
          icon: SweetAlertIcon.SUCCESS,
          text: t('your_account_has_been_successfully_deleted')
        });
        getAccounts();
      } else {
        Swal.fire({
          title: 'Oh...',
          icon: SweetAlertIcon.INFO,
          text: t('error_occurred_delete_account')
        });
      }
    } catch (error) {
      Swal.fire({ icon: SweetAlertIcon.ERROR, title: 'Oppss...', text: error });
    }
  };




  const COLUMNS_CONNECTIONS: GridColDef[] = useMemo(
    () => [
      ...COLUMNS_ACCOUNT_CONNECTIONS(t),
      {
        field: 'delete',
        headerName: t('delete'),
        description: t('delete'),
        align: 'center',
        pinnable: false,
        headerAlign: 'center',
        renderCell: (params) => (
          <Button color="error" onClick={() => handleDeleteAccountSwal(params.row)}>
            {generateIcon('Delete')}
          </Button>
        )
      }
    ],
    []
  );

  useEffect(() => {
    if (token) getAccounts();
  }, [token, getAccounts]);


  return (
    <Stack>
      <Stack className="connections-header connections-stack">
        <h1>{t('connections')}</h1>
        <ButtonGroup
          variant="contained"
          sx={{ border: 'none' }}
          aria-label="Button group with a nested menu">
          <Tooltip title={t('tooltip.addAccount')}>
            <Button className="btn-manage" onClick={handleOnboardingAddAccountClick}>
              <PersonAddIcon />
              {t('account')}
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Stack>
      <TableComponent
        hideFooter
        loading={loading}
        rows={connectedAccounts}
        disableRowSelectionOnClick
        columns={COLUMNS_CONNECTIONS}
        className={classNames('table-connections', mode)}
        slots={{
          noResultsOverlay: () => (
            <Stack className="connections-info">
              <Typography>{error?.message}</Typography>
            </Stack>
          ),
          noRowsOverlay: () => (
            <Stack className="connections-info">
              {error ? (
                <Typography>{error?.message}</Typography>
              ) : (
                <Typography>{t('no_accounts_connected')}</Typography>
              )}
            </Stack>
          )
        }}
      />
    </Stack>
  );
};
